<!-- Workforce.ai (chat) tiered pricing — feature comparison grid -->
<div
  class="tw-flex tw-flex-col tw-mx-auto tw-w-full tw-max-w-5xl tw-p-6 tw-text-gray-900 tw-bg-white dark:tw-bg-gray-800 tw-rounded-lg tw-border tw-border-gray-100 dark:tw-border-gray-400 tw-border-solid tw-shadow xl:tw-p-8">
  <h3 class="tw-text-2xl tw-font-semibold tw-mb-1 tw-text-dark-light-dynamic">
    Workforce.ai Subscription
  </h3>
  <p class="tw-font-light tw-text-gray-500 dark:tw-text-gray-400 tw-mt-0 tw-mb-5">
    Real-Time Workforce Intelligence
  </p>

  <div *ngIf="isLoadingSubscriptions" class="tw-flex tw-justify-center tw-items-center tw-py-10">
    <mat-spinner diameter="50"></mat-spinner>
  </div>

  <ng-container *ngIf="!isLoadingSubscriptions">
    <!-- One card per tier; Pro is highlighted with an orange border + badge -->
    <div class="tw-grid tw-gap-4 tw-grid-cols-1 sm:tw-grid-cols-2 xl:tw-grid-cols-4">
      <div
        *ngFor="let tier of tiers"
        class="tw-relative tw-flex tw-flex-col tw-rounded-xl tw-border-2 tw-border-solid tw-p-5"
        [ngClass]="
          tier.highlighted && !isCurrentTier(tier) && !isTierCanceling(tier)
            ? 'tw-border-orange-400'
            : 'tw-border-gray-200 dark:tw-border-gray-700'
        ">
        <!-- Popular badge (Pro) — dropped once the org is on, or canceling, that tier -->
        <span
          *ngIf="tier.highlighted && !isCurrentTier(tier) && !isTierCanceling(tier)"
          class="tw-absolute tw--top-2.5 tw-left-1/2 tw--translate-x-1/2 tw-bg-orange-400 tw-text-white tw-text-[10px] tw-font-bold tw-tracking-wide tw-uppercase tw-px-3 tw-py-0.5 tw-rounded-full tw-whitespace-nowrap">
          Popular
        </span>

        <!-- Eyebrow + name -->
        <span
          class="tw-text-[11px] tw-font-medium tw-tracking-wider tw-uppercase tw-text-gray-500 dark:tw-text-gray-400">
          {{ tier.eyebrow }}
        </span>
        <span class="tw-text-xl tw-font-semibold tw-text-dark-light-dynamic">{{ tier.name }}</span>

        <!-- Price -->
        <div class="tw-flex tw-items-baseline tw-gap-1 tw-mt-2">
          <span class="tw-text-4xl tw-font-bold tw-leading-none tw-text-dark-light-dynamic">
            {{ tier.priceDisplay }}
          </span>
          <span *ngIf="tier.priceUnit" class="tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
            {{ tier.priceUnit }}
          </span>
        </div>
        <span
          class="tw-block tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mt-1.5 tw-min-h-[1rem] tw-leading-tight">
          {{ tier.priceCaption }}
        </span>

        <hr
          class="tw-w-full tw-my-4 tw-border-0 tw-border-t tw-border-solid tw-border-gray-200 dark:tw-border-gray-700" />

        <!-- Feature list for this tier -->
        <div class="tw-flex tw-flex-col tw-gap-2">
          <div
            *ngFor="let row of featureRows"
            class="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-text-sm">
            <span class="tw-text-gray-500 dark:tw-text-gray-400">
              {{ row.label }}
              <span *ngIf="row.comingSoon" class="tw-text-[10px] tw-italic">(soon)</span>
            </span>
            <span
              class="tw-text-right tw-font-semibold tw-text-dark-light-dynamic"
              [ngClass]="{ 'tw-text-gray-400 dark:tw-text-gray-500': row.comingSoon }">
              {{ row.values[tier.key] }}
            </span>
          </div>
        </div>

        <!-- Scheduled-cancellation note: active now, but ending at term end -->
        <div
          *ngIf="isTierCanceling(tier)"
          class="tw-flex tw-items-center tw-gap-1.5 tw-mt-3 tw-text-xs tw-font-medium tw-text-amber-600 dark:tw-text-amber-400">
          <mat-icon class="tw-text-sm tw-w-4 tw-h-4 tw-leading-none">schedule</mat-icon>
          <span>Cancels on {{ tierEndsAt(tier) | date: 'mediumDate' }}</span>
        </div>

        <!-- Spacer keeps the CTA pinned to the bottom so buttons align across cards -->
        <div class="tw-flex-1 tw-min-h-[1.5rem]"></div>

        <!-- CTA -->
        <button
          (click)="onTierAction(tier)"
          [disabled]="actionDisabled(tier) || !(isOrgAdmin$ | async)"
          [ngClass]="
            isCurrentTier(tier)
              ? 'tw-bg-transparent tw-text-gray-400 dark:tw-text-gray-500 tw-border-gray-200 dark:tw-border-gray-700'
              : tier.highlighted
                ? 'tw-bg-orange-400 hover:tw-bg-orange-500 tw-text-white tw-border-orange-400'
                : 'tw-bg-transparent tw-text-teal-600 dark:tw-text-teal-400 tw-border-gray-300 dark:tw-border-gray-600 hover:tw-bg-gray-100 dark:hover:tw-bg-gray-700/50'
          "
          class="tw-w-full tw-px-3 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-semibold tw-border tw-border-solid tw-cursor-pointer tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
          {{ ctaLabel(tier) }}
        </button>
      </div>
    </div>

    <app-alert *ngIf="!(isOrgAdmin$ | async)" alertType="info" class="tw-mt-4">
      You must be an administrator to manage your subscription.
    </app-alert>
  </ng-container>
</div>
